import { AMBIGUOUS_DISTRICTS, DISTRICT_ALIASES, DISTRICT_TO_STATE, EXCLUDED_FROM_REGEX } from './district-data';
import { locations } from '@/lib/seo/locations';

/**
 * Google-style typeahead entries for the nationwide search surfaces. Every
 * suggestion maps to text the deterministic parser resolves on its own, so a
 * selection never depends on browser-supplied court codes.
 */
export interface CourtLocationSuggestion {
  /** What the visitor sees in the dropdown. */
  display: string;
  /** Appended to the query; must resolve through the server-side parser. */
  searchText: string;
  /** Lowercase tokens used for matching. */
  keywords: string;
}

function titleCase(value: string): string {
  return value.split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function buildSuggestions(): CourtLocationSuggestion[] {
  const bySearchText = new Map<string, CourtLocationSuggestion>();
  const add = (entry: CourtLocationSuggestion) => {
    const key = entry.searchText.toLowerCase();
    if (!bySearchText.has(key)) bySearchText.set(key, entry);
  };

  // High Courts and the Supreme Court from the server-owned page catalogue.
  for (const location of locations) {
    if (location.courtType === 'High Court' && location.highCourtName) {
      add({
        display: location.highCourtName,
        searchText: location.highCourtName,
        keywords: `${location.highCourtName} ${location.cityName} ${location.stateName} high court`.toLowerCase(),
      });
    }
    if (location.courtType === 'Supreme Court') {
      add({
        display: 'Supreme Court of India',
        searchText: 'Supreme Court of India',
        keywords: 'supreme court of india new delhi sci',
      });
    }
  }

  // Every eCourts district. The parser already knows district → state, so the
  // state goes into the search text only when the district name is ambiguous.
  for (const [district, state] of Object.entries(DISTRICT_TO_STATE)) {
    if (EXCLUDED_FROM_REGEX.has(district)) continue;
    const name = titleCase(district);
    const states = Array.isArray(state) ? state : [state];
    const ambiguous = AMBIGUOUS_DISTRICTS.has(district) || states.length > 1;
    for (const stateName of states) {
      add({
        display: `${name} — District Court, ${stateName}`,
        searchText: ambiguous ? `${name} district court ${stateName}` : `${name} district court`,
        keywords: `${name} ${stateName} district court`.toLowerCase(),
      });
    }
  }

  // Renamed cities and common alternate spellings. The alias keywords join the
  // canonical entry so "Ahilyanagar" finds the eCourts Ahmednagar district.
  for (const [alias, target] of Object.entries(DISTRICT_ALIASES)) {
    const canonicalState = target.state;
    const canonicalName = target.district;
    const aliasName = titleCase(alias);
    if (aliasName.toLowerCase() === canonicalName.toLowerCase()) continue;
    const searchText = `${canonicalName} district court`;
    const existing = bySearchText.get(searchText.toLowerCase());
    if (existing) {
      existing.keywords = `${existing.keywords} ${aliasName.toLowerCase()}`;
      if (!existing.display.includes('(')) {
        existing.display = `${canonicalName} (${aliasName}) — District Court, ${canonicalState}`;
      }
      continue;
    }
    add({
      display: `${aliasName} (${canonicalName}) — District Court, ${canonicalState}`,
      searchText,
      keywords: `${aliasName} ${canonicalName} ${canonicalState} district court`.toLowerCase(),
    });
  }

  return [...bySearchText.values()];
}

export const COURT_LOCATION_SUGGESTIONS: CourtLocationSuggestion[] = buildSuggestions();

/**
 * Word-prefix matching: every query token must prefix a word of the entry.
 * Entries whose display starts with the query rank first, then keyword-word
 * prefixes, then alphabetical — close to how a search box is expected to feel.
 */
export function filterCourtLocationSuggestions(query: string, limit = 8): CourtLocationSuggestion[] {
  const tokens = query.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const scored: Array<{ entry: CourtLocationSuggestion; score: number }> = [];
  for (const entry of COURT_LOCATION_SUGGESTIONS) {
    const words = entry.keywords.split(/[^a-z0-9]+/).filter(Boolean);
    const everyTokenMatches = tokens.every(token => words.some(word => word.startsWith(token)));
    if (!everyTokenMatches) continue;
    const displayLower = entry.display.toLowerCase();
    const score = displayLower.startsWith(tokens.join(' ')) ? 2 : displayLower.split(/[^a-z0-9]+/)[0]?.startsWith(tokens[0]) ? 1 : 0;
    scored.push({ entry, score });
  }

  return scored
    .sort((a, b) => b.score - a.score || a.entry.display.localeCompare(b.entry.display))
    .slice(0, Math.max(1, limit))
    .map(item => item.entry);
}
